import type { EraDef } from '../types';

/**
 * ERA 5 — STONE AGE
 * Identity: a war party — traps underfoot, shield walls in front, fire from
 * above. Movement identity: "the ground itself is your enemy — hunters wait
 * buried beneath you, while the ones you can see hide behind shields you
 * have to flank." Pattern identity: arcing thrown weapons — curving bolas,
 * short spear-line volleys, and thrown torches that leave burning ground.
 * Era hazard: fire pools everywhere (linger from several patterns + the
 * boss's quake). Signature behaviors owned here: `ambush`, `shieldWall`.
 * Palette: ochre/ember savanna dusk, warm firelight. Enemy bullets: hot
 * orange/red (contrasts the cyan player bullets). Hazards: ember orange.
 */
const ENEMY_BULLET = 0xff5a2a;
const HAZARD = 0xff8a2a;

export const ERA_STONEAGE: EraDef = {
  index: 4,
  id: 'stoneage',
  name: 'Stone Age',
  subtitle: '10,000 years ago',
  implemented: true,
  palette: {
    sky: 0x2a2016,
    fog: 0x3a2c1c,
    fogDensity: 0.018,
    ground: 0x5c4530,
    groundAlt: 0x6f5238,
    ambient: 0x8a6a44,
    sun: 0xffcf8a,
    sunIntensity: 1.4,
    accent: 0x7df9ff,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'savanna',
    arenaRadius: 64,
    groundStyle: 'cracked',
    props: [
      { visual: { geo: 'box', color: 0x6f5238, emissive: 0x2a1c10, emissiveIntensity: 0.2, scale: 1 }, count: 22, scale: [0.5, 1.6], y: 0 },
      { visual: { geo: 'cone', color: 0xff8a2a, emissive: 0xff5a2a, emissiveIntensity: 0.8, anim: 'pulse' }, count: 12, scale: [0.4, 0.9], y: 0.3 },
      { visual: { geo: 'capsule', color: 0x4a3320, scaleY: 3, anim: 'none' }, count: 10, scale: [0.4, 0.9], y: 0.8 },
    ],
  },
  enemies: [
    {
      id: 's5_runner',
      name: 'Tribe Runner',
      archetype: 'swarmer',
      hp: 18,
      speed: 4.4,
      damage: 9,
      radius: 0.4,
      xp: 3,
      visual: { geo: 'capsule', color: 0xa06a3c, emissive: 0x4a2a12, emissiveIntensity: 0.3, scale: 0.9 },
      // Filler swarmer: sprints straight at you in packs. Counterplay: cheap individually, don't let them stack contact hits.
      behavior: { type: 'chase' },
    },
    {
      id: 's5_staked_hunter',
      name: 'Staked Hunter',
      archetype: 'charger',
      hp: 42,
      speed: 4.2,
      damage: 15,
      radius: 0.5,
      xp: 7,
      visual: { geo: 'spike', color: 0x7a5a34, emissive: 0x3a2410, emissiveIntensity: 0.35, scale: 1, anim: 'none' },
      // Buried and untargetable until you walk within range, then erupts with a flash and a brief contact
      // grace period before chasing. Counterplay: the rise is telegraphed — back off when the ground flashes.
      behavior: { type: 'ambush', triggerRange: 3.2, riseTime: 0.45 },
    },
    {
      id: 's5_shield_warrior',
      name: 'Shield Warrior',
      archetype: 'tank',
      hp: 70,
      speed: 1.6,
      damage: 14,
      radius: 0.6,
      xp: 9,
      shardChance: 0.1,
      visual: { geo: 'box', color: 0x6a5030, emissive: 0x241a0c, emissiveIntensity: 0.3, scale: 1.1 },
      // Advances slowly behind a raised shield, always facing you; frontal hits are mostly absorbed.
      // Counterplay: get behind it and flank — fighting the stationary-fire rule is the whole point.
      behavior: { type: 'shieldWall', speedMult: 1, frontArc: 2.2, frontReduction: 0.8 },
    },
    {
      id: 's5_spear_thrower',
      name: 'Spear Thrower',
      archetype: 'shooter',
      hp: 46,
      speed: 2.7,
      damage: 12,
      radius: 0.55,
      xp: 8,
      visual: { geo: 'cone', color: 0x8a5a2a, emissive: 0x3a1e0c, emissiveIntensity: 0.4, scale: 1 },
      // Kites at range, alternating a short line-volley of spears and a curving bola. Counterplay: the wall
      // is straight and readable, but the bola bends toward your last position — juke sideways, not straight back.
      behavior: { type: 'keepDistance', distance: 10, strafe: 0.3 },
      patterns: ['s5_spear_wall', 's5_bola_curve'],
      patternCooldown: 1.8,
      fireRange: 16,
    },
    {
      id: 's5_torchbearer',
      name: 'Torchbearer',
      archetype: 'shooter',
      hp: 40,
      speed: 2.3,
      damage: 10,
      radius: 0.55,
      xp: 8,
      visual: { geo: 'torus', color: 0xa8622a, emissive: 0xff8a2a, emissiveIntensity: 0.7, scale: 1, anim: 'spin' },
      // Lobs slow thrown torches that leave a burning fire pool on landing. Counterplay: the arc is slow —
      // dodge the throw, then stay off the ground it lands on, not just where you were standing.
      behavior: { type: 'keepDistance', distance: 8, strafe: 0.2 },
      patterns: ['s5_torch_lob'],
      patternCooldown: 2.2,
      fireRange: 14,
    },
    {
      id: 's5_bone_totem',
      name: 'Bone Totem',
      archetype: 'shooter',
      hp: 55,
      speed: 0,
      damage: 8,
      radius: 0.65,
      xp: 10,
      shardChance: 0.15,
      visual: { geo: 'spike', color: 0xd9c9a0, emissive: 0x8a6a2a, emissiveIntensity: 0.4, scale: 1.15, anim: 'wobble' },
      // Stationary ritual totem: rings of curving bone-shard bolas expand outward. Counterplay: gaps in the
      // ring are stable — find one and hold it rather than trying to outrun the curve.
      behavior: { type: 'stationary' },
      patterns: ['s5_totem_ring'],
      patternCooldown: 2.4,
      fireRange: 15,
    },
    {
      id: 's5_war_chief',
      name: 'War Chief',
      archetype: 'elite',
      hp: 130,
      speed: 3.6,
      damage: 20,
      radius: 0.7,
      xp: 20,
      shardChance: 0.4,
      elite: true,
      visual: { geo: 'box', color: 0xff9a4a, emissive: 0xff5a2a, emissiveIntensity: 0.9, scale: 1.3, anim: 'none' },
      // Elite: a fast shieldWall that closes ground quicker than the rank-and-file warrior, and throws spear
      // walls while advancing. Counterplay: same flanking rule, but it catches up faster if you circle wide.
      behavior: { type: 'shieldWall', speedMult: 1.7, frontArc: 2.0, frontReduction: 0.75 },
      patterns: ['s5_spear_wall'],
      patternCooldown: 2,
      fireRange: 14,
    },
  ],
  patterns: [
    // ---- enemy patterns ----
    {
      // Short spear line — a wall of bullets advancing together, with gaps to dodge through.
      id: 's5_spear_wall',
      emitters: [{ shape: 'wall', interval: 0.6, bursts: 2, count: 9, spread: 8, gaps: 2, bullet: { speed: 10, lifetime: 1.3, radius: 0.22, damage: 12, visual: 'needle', color: ENEMY_BULLET } }],
    },
    {
      // Curving bola: constant angular velocity bends it around the player mid-flight — era 5's pattern signature.
      id: 's5_bola_curve',
      emitters: [
        { shape: 'aimed', interval: 0.5, bursts: 2, count: 2, spread: 0.5, angle: 0.35, bullet: { speed: 7, curve: 1.4, lifetime: 2.6, radius: 0.26, damage: 10, visual: 'shard', color: ENEMY_BULLET } },
        { shape: 'aimed', interval: 0.5, start: 0.25, bursts: 2, count: 2, spread: 0.5, angle: -0.35, bullet: { speed: 7, curve: -1.4, lifetime: 2.6, radius: 0.26, damage: 10, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    {
      // Slow thrown torch that lands and leaves a burning fire pool — the era's ground hazard signature.
      id: 's5_torch_lob',
      emitters: [{ shape: 'aimed', interval: 1, bursts: 2, count: 1, spread: 0.3, bullet: { speed: 6, accel: -2.4, minSpeed: 1.5, lifetime: 1.6, radius: 0.26, damage: 9, visual: 'blob', color: HAZARD, linger: { radius: 1.1, duration: 3.5, dps: 8, color: HAZARD } } }],
    },
    {
      // Ring of curving bone-shard bolas — expands with a stable gap pattern.
      id: 's5_totem_ring',
      emitters: [
        { shape: 'radial', interval: 2.4, bursts: 1, count: 10, bullet: { speed: 4.5, curve: 0.9, lifetime: 3, radius: 0.24, damage: 9, visual: 'shard', color: ENEMY_BULLET } },
      ],
    },
    // ---- boss patterns ----
    {
      // Phase 1: readable aimed spear volleys, teaching the fan.
      id: 's5_chief_spear',
      emitters: [{ shape: 'aimed', interval: 0.85, bursts: 3, count: 3, spread: 0.5, bullet: { speed: 11, lifetime: 1.6, radius: 0.24, damage: 12, visual: 'needle', color: ENEMY_BULLET } }],
    },
    {
      // Phase 1: a slow, wide curving bola ring — telegraphed and dodgeable at a walk.
      id: 's5_chief_bola_ring',
      emitters: [{ shape: 'radial', interval: 1.8, bursts: 1, count: 12, bullet: { speed: 4, curve: 0.7, lifetime: 3.4, radius: 0.28, damage: 11, visual: 'shard', color: ENEMY_BULLET } }],
    },
    {
      // Phase-1 onEnter warcry: a wide ring shockwave announcing the fight.
      id: 's5_chief_warcry',
      emitters: [{ shape: 'radial', interval: 1, bursts: 1, count: 16, bullet: { speed: 3.4, lifetime: 1.3, radius: 0.42, damage: 12, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      // Phase 2: sparse spear wall — deliberately thin so the quake zones read clearly.
      id: 's5_chief_spear_sparse',
      emitters: [{ shape: 'wall', interval: 1.3, bursts: 2, count: 9, spread: 9, gaps: 3, bullet: { speed: 9, lifetime: 1.6, radius: 0.24, damage: 13, visual: 'needle', color: ENEMY_BULLET } }],
    },
    {
      // Phase 3: tighter, faster bola curves — the final-phase pressure alongside shieldWall + quake.
      id: 's5_chief_bola_tight',
      duration: 2.4,
      emitters: [{ shape: 'spiral', interval: 0.18, bursts: 0, count: 3, rotatePerBurst: 0.3, bullet: { speed: 6, curve: 1.6, lifetime: 2.4, radius: 0.24, damage: 12, visual: 'shard', color: ENEMY_BULLET } }],
    },
    {
      // Phase-3 onEnter burst: torches thrown in every direction, seeding fire across the arena.
      id: 's5_chief_phase3_enter',
      emitters: [
        { shape: 'random', interval: 1, bursts: 1, count: 10, spread: 6.283, bullet: { speed: 6, accel: -2.6, minSpeed: 1, lifetime: 1.8, radius: 0.3, damage: 14, visual: 'blob', color: HAZARD, linger: { radius: 1.3, duration: 4, dps: 9, color: HAZARD } } },
      ],
    },
  ],
  boss: {
    id: 's5_chieftain',
    name: 'Chieftain Ashborn',
    title: 'Warlord of the Rival Tribe',
    hp: 3700,
    radius: 2.2,
    speed: 1.8,
    damage: 26,
    xp: 420,
    visual: { geo: 'box', color: 0x7a4a24, emissive: 0xff5a2a, emissiveIntensity: 0.5, scale: 1.5, anim: 'none' },
    phases: [
      {
        // Phase 1 "Warcry" — teaching phase: plain chase + kiting spear/bola, opened with a warcry ring.
        hpBelow: 1,
        name: 'Warcry',
        behavior: { type: 'chase' },
        patterns: ['s5_chief_spear', 's5_chief_bola_ring'],
        patternCooldown: 1.6,
        transition: 1.5,
        onEnter: 's5_chief_warcry',
      },
      {
        // Phase 2 "Firestorm" — adds quake (fire-arrow rain) and spear-thrower summons; bullet patterns
        // sparsen so the quake zones read as the real threat.
        hpBelow: 0.65,
        name: 'Firestorm',
        behavior: { type: 'keepDistance', distance: 11, strafe: 0.25 },
        patterns: ['s5_chief_spear_sparse'],
        patternCooldown: 2.6,
        special: { type: 'quake', interval: 7, count: 3, telegraph: 1.1, radius: 3, dps: 26, duration: 3, spread: 8 },
        summons: { enemy: 's5_spear_thrower', count: 2, interval: 8 },
        transition: 2,
        speedMult: 1.15,
      },
      {
        // Phase 3 "Last Stand" — the chief turtles behind his own shield (must be flanked) while a shorter
        // quake cadence and tight bola spirals keep the pressure on; opened with a torch burst.
        hpBelow: 0.3,
        name: 'Last Stand',
        behavior: { type: 'shieldWall', speedMult: 1.1, frontArc: 2.1, frontReduction: 0.8 },
        patterns: ['s5_chief_bola_tight'],
        patternCooldown: 1.4,
        special: { type: 'quake', interval: 5.5, count: 4, telegraph: 1.0, radius: 3, dps: 28, duration: 3, spread: 7 },
        summons: { enemy: 's5_staked_hunter', count: 2, interval: 9 },
        transition: 2,
        speedMult: 1.1,
        onEnter: 's5_chief_phase3_enter',
      },
    ],
  },
  stages: [
    {
      name: 'Ochre Flats',
      duration: 210,
      objective: { type: 'defend', seconds: 150, health: 100, radius: 7 },
      density: { start: 0.8, end: 2.1 },
      pool: [
        { enemy: 's5_runner', weight: 8 },
        { enemy: 's5_staked_hunter', weight: 3, minTime: 20 },
        { enemy: 's5_spear_thrower', weight: 3, minTime: 30 },
        { enemy: 's5_torchbearer', weight: 2, minTime: 45 },
        { enemy: 's5_shield_warrior', weight: 3, minTime: 60 },
      ],
      waves: [
        { t: 4, enemy: 's5_runner', count: 4, formation: 'ring' },
        { t: 25, enemy: 's5_staked_hunter', count: 3, formation: 'random' },
        { t: 50, enemy: 's5_spear_thrower', count: 3, formation: 'line' },
        { t: 75, enemy: 's5_runner', count: 12, formation: 'cluster' },
        { t: 100, enemy: 's5_shield_warrior', count: 2, formation: 'edge' },
        { t: 130, enemy: 's5_torchbearer', count: 2, formation: 'edge' },
        { t: 160, enemy: 's5_staked_hunter', count: 4, formation: 'random' },
        { t: 185, enemy: 's5_war_chief', count: 1, formation: 'edge', elite: true },
      ],
    },
    {
      name: 'Firelit Camp',
      duration: 240,
      density: { start: 1.05, end: 2.4 },
      pool: [
        { enemy: 's5_runner', weight: 6 },
        { enemy: 's5_shield_warrior', weight: 6 },
        { enemy: 's5_spear_thrower', weight: 4 },
        { enemy: 's5_torchbearer', weight: 2 },
        { enemy: 's5_staked_hunter', weight: 3 },
        { enemy: 's5_bone_totem', weight: 3, minTime: 50 },
      ],
      waves: [
        { t: 5, enemy: 's5_shield_warrior', count: 3, formation: 'ring' },
        { t: 40, enemy: 's5_staked_hunter', count: 5, formation: 'random' },
        { t: 75, enemy: 's5_torchbearer', count: 4, formation: 'ring' },
        { t: 105, enemy: 's5_war_chief', count: 1, formation: 'edge', elite: true },
        { t: 135, enemy: 's5_runner', count: 16, formation: 'ring' },
        { t: 165, enemy: 's5_bone_totem', count: 3, formation: 'edge' },
        { t: 195, enemy: 's5_shield_warrior', count: 3, formation: 'cluster' },
        { t: 220, enemy: 's5_war_chief', count: 1, formation: 'line', elite: true },
      ],
    },
    {
      name: "The Chieftain's Fire",
      duration: 600,
      density: { start: 1.2, end: 1.2 },
      pool: [
        { enemy: 's5_runner', weight: 6 },
        { enemy: 's5_spear_thrower', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 's5_runner', count: 10, formation: 'ring' }],
      boss: { id: 's5_chieftain', at: 20 },
    },
  ],
  loot: [
    { itemId: 'a_ashguard', weight: 2 },
    { itemId: 'r_ember_totem', weight: 2 },
    { itemId: 'w_bronze_bow', weight: 2 },
    { itemId: 'a_hide', weight: 1 },
    { itemId: 'r_amber', weight: 1 },
  ],
  audio: { music: 'music/era5_stoneage', ambient: 'amb/savanna', sfxSet: 'tribal' },
};
